import { type FormEvent, useEffect, useRef, useState } from "react";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { EphemeralStartDialog, EphemeralStatusBadge } from "@/components/ephemeral-start-dialog";
import { MachineTable, TypeBadge } from "@/components/machine-table";
import { PoolPurposeBadge } from "@/components/pool-purpose-badge";
import { ProviderLogo } from "@/components/provider-logo";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useAuth } from "@/contexts/auth-context";
import { useCanEditSpotPool } from "@/hooks/use-admin-settings";
import { usePageTitle } from "@/hooks/use-page-title";
import { useProvisioningLogs } from "@/hooks/use-provisioning-logs";
import {
  comboKey,
  formatAllowedCombos,
  parseAllowedCombos,
  useAvailableCombos,
  useCreateSpotTarget,
  useDeleteSpotTarget,
  useSpotTargets,
  useTargetMachines,
  useUpdateSpotTarget,
} from "@/hooks/use-spot-targets";
import { formatAge } from "@/lib/format";
import { poolPurposeLabel } from "@/lib/pool-purpose";
import type { AvailableCombo, PoolPurpose, SpotTarget } from "@/lib/types";
import { cn } from "@/lib/utils";

const DAYS_OF_WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

const COMMON_TIMEZONES = [
  "UTC",
  "Asia/Jerusalem",
  "America/Los_Angeles",
  "America/New_York",
  "America/Chicago",
  "Europe/London",
  "Europe/Berlin",
  "Asia/Tokyo",
  "Asia/Singapore",
];

// Mirrors the server's SPOT_MAX_TARGET_COUNT (fleet/constants.py). Advisory
// only — the API rejects out-of-range counts with 422 regardless.
const MAX_TARGET_COUNT = 32;

function PurposeSelector({
  value,
  onChange,
  disabled = false,
}: {
  value: PoolPurpose;
  onChange: (purpose: PoolPurpose) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-muted-foreground">Intended use</Label>
      <Select
        value={value}
        disabled={disabled}
        onValueChange={(next) => {
          if (next === "rd" || next === "ci") onChange(next);
        }}
      >
        <SelectTrigger
          aria-label="Pool intended use"
          className="w-full border-border bg-card text-foreground"
        >
          <SelectValue>{poolPurposeLabel(value)}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="rd">R&amp;D</SelectItem>
          <SelectItem value="ci">CI testing</SelectItem>
        </SelectContent>
      </Select>
      {disabled && <p className="text-[11px] text-muted-foreground">Fixed for spot pools.</p>}
    </div>
  );
}

function parseDays(raw: string): Set<string> {
  return new Set(
    raw
      .split(",")
      .map((day) => day.trim())
      .filter(Boolean),
  );
}

function formatDays(days: Set<string>): string {
  return DAYS_OF_WEEK.filter((day) => days.has(day)).join(",");
}

function ScheduleEditor({
  days,
  onDaysChange,
  start,
  onStartChange,
  end,
  onEndChange,
  timezone,
  onTimezoneChange,
}: {
  days: Set<string>;
  onDaysChange: (next: Set<string>) => void;
  start: string;
  onStartChange: (next: string) => void;
  end: string;
  onEndChange: (next: string) => void;
  timezone: string;
  onTimezoneChange: (next: string) => void;
}) {
  const timezoneOptions = COMMON_TIMEZONES.includes(timezone)
    ? COMMON_TIMEZONES
    : [...COMMON_TIMEZONES, timezone];

  return (
    <div className="space-y-3 rounded-lg border border-border bg-accent/30 p-3">
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-muted-foreground">Active days</Label>
        <ToggleGroup
          multiple
          value={DAYS_OF_WEEK.filter((day) => days.has(day))}
          onValueChange={(value) => onDaysChange(new Set(value))}
          className="flex flex-wrap gap-1"
        >
          {DAYS_OF_WEEK.map((day) => (
            <ToggleGroupItem
              key={day}
              value={day}
              className="h-7 min-w-[40px] px-2 text-[12px] text-foreground/70 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
            >
              {day}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground">Start (HH:MM)</Label>
          <Input
            type="time"
            value={start}
            onChange={(event) => onStartChange(event.target.value)}
            className="border-border bg-card text-foreground"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground">End (HH:MM)</Label>
          <Input
            type="time"
            value={end}
            onChange={(event) => onEndChange(event.target.value)}
            className="border-border bg-card text-foreground"
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-muted-foreground">Timezone</Label>
        <Select value={timezone} onValueChange={(value) => value && onTimezoneChange(value)}>
          <SelectTrigger className="w-full border-border bg-card text-foreground">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="border-border bg-card">
            {timezoneOptions.map((option) => (
              <SelectItem
                key={option}
                value={option}
                className="text-foreground/80 focus:bg-accent focus:text-foreground"
              >
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function groupCombosByRegion(combos: AvailableCombo[]): Map<string, AvailableCombo[]> {
  const grouped = new Map<string, AvailableCombo[]>();
  for (const combo of combos) {
    const list = grouped.get(combo.region) ?? [];
    list.push(combo);
    grouped.set(combo.region, list);
  }
  return grouped;
}

function ComboSelector({
  gpuType,
  gpuCount,
  selected,
  onChange,
  enabled,
}: {
  gpuType: string;
  gpuCount: number;
  /** Set of selected combo keys (`region/fabric` or `region`). Empty Set = "all". */
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  enabled: boolean;
}) {
  const { data, isLoading, error } = useAvailableCombos("nebius", gpuType, gpuCount, enabled);

  if (!enabled) return null;

  if (isLoading) {
    return (
      <div className="rounded-lg border border-border bg-accent/30 p-3 text-[12px] text-muted-foreground">
        Loading available zones &amp; fabrics…
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-600">
        Failed to load combos: {error instanceof Error ? error.message : "unknown error"}
      </div>
    );
  }
  const combos = data?.combos ?? [];
  if (combos.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-accent/30 p-3 text-[12px] text-muted-foreground">
        No (region, fabric) combos exist for this spec in the regions enabled under Settings.
      </div>
    );
  }

  const allKeys = combos.map((combo) => comboKey(combo.region, combo.fabric));
  // Empty `selected` = "all combos allowed" (NULL in backend). Render that as
  // every checkbox checked so the user sees current effective state.
  const effective = selected.size === 0 ? new Set(allKeys) : selected;

  const commit = (nextEffective: Set<string>) => {
    // If every combo is on, collapse back to "all" (empty Set → backend NULL)
    // so newly-added fabrics upstream are automatically opted in.
    const isAll = allKeys.every((key) => nextEffective.has(key));
    onChange(isAll ? new Set() : nextEffective);
  };

  const grouped = groupCombosByRegion(combos);
  const regions = Array.from(grouped.keys()).sort();
  // Stored combos Nebius no longer offers, or in a region an admin disabled. The
  // backend keeps accepting them; list them so they can be dropped.
  const staleKeys = [...selected].filter((key) => !allKeys.includes(key)).sort();
  const currentSelected = [...selected].filter((key) => allKeys.includes(key));

  return (
    <div className="space-y-3 rounded-lg border border-border bg-accent/30 p-3">
      <div className="flex items-baseline justify-between">
        <Label className="text-xs font-medium text-muted-foreground">
          Allowed zones &amp; fabrics
        </Label>
        <span className="text-[10px] text-muted-foreground">
          {selected.size === 0
            ? "All combos (default)"
            : `${currentSelected.length} of ${combos.length} selected`}
        </span>
      </div>
      {staleKeys.length > 0 ? (
        <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
          <span>Also allowed, not offered in an enabled region: {staleKeys.join(", ")}</span>
          {/* Dropping every stored key would read as "all combos", so keep one current one. */}
          {currentSelected.length > 0 ? (
            <button
              type="button"
              className="shrink-0 underline hover:text-foreground"
              onClick={() => onChange(new Set(currentSelected))}
            >
              Remove
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="space-y-2">
        {regions.map((region) => {
          const regionCombos = grouped.get(region) ?? [];
          const regionKeys = regionCombos.map((combo) => comboKey(combo.region, combo.fabric));
          const allRegionSelected = regionKeys.every((key) => effective.has(key));
          const someRegionSelected = regionKeys.some((key) => effective.has(key));
          const toggleRegion = () => {
            const next = new Set(effective);
            for (const key of regionKeys) {
              if (allRegionSelected) {
                next.delete(key);
              } else {
                next.add(key);
              }
            }
            commit(next);
          };
          return (
            <div key={region} className="space-y-1">
              <button
                type="button"
                onClick={toggleRegion}
                className={cn(
                  "cursor-pointer text-[11px] font-medium uppercase tracking-wide transition-colors hover:text-foreground",
                  allRegionSelected
                    ? "text-foreground"
                    : someRegionSelected
                      ? "text-amber-600"
                      : "text-muted-foreground line-through",
                )}
              >
                {region}
                <span className="ml-1.5 text-[9px] font-normal opacity-70">(toggle all)</span>
              </button>
              <ToggleGroup
                multiple
                value={regionKeys.filter((key) => effective.has(key))}
                onValueChange={(values) => {
                  // `values` is the new selection for this region only — merge
                  // with the current selection of every other region.
                  const next = new Set(effective);
                  for (const key of regionKeys) next.delete(key);
                  for (const key of values) next.add(key);
                  commit(next);
                }}
                className="flex flex-wrap gap-1"
              >
                {regionCombos.map((combo) => {
                  const key = comboKey(combo.region, combo.fabric);
                  return (
                    <ToggleGroupItem
                      key={key}
                      value={key}
                      className="h-7 px-2 text-[12px] text-foreground/70 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                    >
                      {combo.fabric ?? "(no fabric)"}
                    </ToggleGroupItem>
                  );
                })}
              </ToggleGroup>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProvisioningLogsDialog({
  target,
  label = "Provisioning...",
  triggerClassName = "text-amber-600 decoration-amber-300 hover:text-amber-700 hover:decoration-amber-400",
}: {
  target: SpotTarget;
  label?: string;
  triggerClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const { logs, error: logsError } = useProvisioningLogs(target.id, open);
  const scrollRef = useRef<HTMLPreElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll on new logs
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [logs]);

  const levelColor = (level: string) => {
    if (level === "error") return "text-rose-600";
    if (level === "warning") return "text-amber-600";
    return "text-foreground/80";
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <button
            type="button"
            className={cn(
              "cursor-pointer text-[12px] font-medium underline underline-offset-2 transition-colors",
              triggerClassName,
            )}
          />
        }
      >
        {label}
      </DialogTrigger>
      <DialogContent className="border-border bg-card sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-foreground">
            Provisioning Logs &mdash; {target.gpu_count}&times; {target.gpu_type} (
            {target.provider.toUpperCase()})
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Real-time provisioning output for this pool.
          </DialogDescription>
        </DialogHeader>
        <pre
          ref={scrollRef}
          className="max-h-[400px] min-h-[200px] overflow-auto rounded-lg border border-border bg-accent p-3 font-mono text-[12px] leading-5"
        >
          {logsError && <div className="mb-2 text-rose-600">[error] {logsError}</div>}
          {logs.length === 0 && !logsError ? (
            <span className="text-muted-foreground">No logs yet. Waiting for provisioner...</span>
          ) : (
            logs.map((entry, index) => (
              <div key={`${entry.timestamp}-${index}`} className={levelColor(entry.level)}>
                <span className="mr-2 text-muted-foreground">
                  {new Date(entry.timestamp).toLocaleTimeString("en-US", {
                    hour12: false,
                  })}
                </span>
                <span className="mr-2 text-[10px] font-semibold uppercase opacity-60">
                  [{entry.level}]
                </span>
                {entry.message}
              </div>
            ))
          )}
        </pre>
      </DialogContent>
    </Dialog>
  );
}

const GPU_CATALOG: Record<string, number[]> = {
  H100: [1, 8],
  H200: [1, 8],
  B200: [1, 8],
  B300: [1, 8],
};

function coerceGpuCount(gpuType: string, gpuCount: string): string {
  const counts = GPU_CATALOG[gpuType] ?? [];
  return counts.includes(Number(gpuCount)) ? gpuCount : String(counts[counts.length - 1] ?? "");
}

function AddTargetDialog() {
  const [open, setOpen] = useState(false);
  const [pricing, setPricing] = useState<"spot" | "ondemand">("spot");
  const [gpuType, setGpuType] = useState("H100");
  const [gpuCount, setGpuCount] = useState("8");
  const [targetCount, setTargetCount] = useState("1");
  const [labelText, setLabelText] = useState("");
  const [purpose, setPurpose] = useState<PoolPurpose>("rd");
  const [activeDays, setActiveDays] = useState<Set<string>>(
    new Set(["Mon", "Tue", "Wed", "Thu", "Fri"]),
  );
  const [activeStart, setActiveStart] = useState("08:00");
  const [activeEnd, setActiveEnd] = useState("20:00");
  const [activeTz, setActiveTz] = useState("UTC");
  const [allowedCombos, setAllowedCombos] = useState<Set<string>>(new Set());
  const [packFabrics, setPackFabrics] = useState(false);
  const [error, setError] = useState("");
  const createTarget = useCreateSpotTarget();
  const packable = canPackFabrics(Number(gpuCount), pricing);

  const reset = () => {
    setPricing("spot");
    setGpuType("H100");
    setGpuCount("8");
    setTargetCount("1");
    setLabelText("");
    setPurpose("rd");
    setActiveDays(new Set(["Mon", "Tue", "Wed", "Thu", "Fri"]));
    setActiveStart("08:00");
    setActiveEnd("20:00");
    setActiveTz("UTC");
    setAllowedCombos(new Set());
    setPackFabrics(false);
    setError("");
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setError("");
    if (activeDays.size === 0) {
      setError("Select at least one active day");
      return;
    }
    if (activeStart === activeEnd) {
      setError("Start and end times must differ");
      return;
    }
    createTarget.mutate(
      {
        provider: "nebius",
        gpu_type: gpuType,
        gpu_count: Number(gpuCount),
        target_count: Number(targetCount),
        pricing,
        label: labelText || undefined,
        purpose,
        active_days: formatDays(activeDays),
        active_start: activeStart,
        active_end: activeEnd,
        active_tz: activeTz,
        // Only send when user actually restricted; empty Set = "all" so omit
        // the field entirely and let the backend store NULL.
        ...(allowedCombos.size > 0 ? { allowed_combos: formatAllowedCombos(allowedCombos) } : {}),
        ...(packable && packFabrics ? { pack_fabrics: true } : {}),
      },
      {
        onSuccess: () => {
          reset();
          setOpen(false);
        },
        onError: (err) => {
          setError(err instanceof Error ? err.message : "Failed to create target");
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) reset();
      }}
    >
      <DialogTrigger
        render={
          <Button
            variant="outline"
            className="border-border bg-card text-foreground/80 hover:bg-accent hover:text-foreground"
          />
        }
      >
        + Add Pool
      </DialogTrigger>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto border-border bg-card sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground">Add Pool</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Define a new GPU pool for the auto-provisioner.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <PurposeSelector value={purpose} onChange={setPurpose} />
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Label (optional)</Label>
            <Input
              value={labelText}
              onChange={(event) => setLabelText(event.target.value)}
              placeholder="e.g. H100 pool"
              className="border-border bg-card text-foreground placeholder:text-muted-foreground"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Pricing</Label>
            <Select
              value={pricing}
              onValueChange={(value) => value && setPricing(value as "spot" | "ondemand")}
            >
              <SelectTrigger className="w-full border-border bg-card text-foreground">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-border bg-card">
                <SelectItem
                  value="spot"
                  className="text-foreground/80 focus:bg-accent focus:text-foreground"
                >
                  Spot
                </SelectItem>
                <SelectItem
                  value="ondemand"
                  className="text-foreground/80 focus:bg-accent focus:text-foreground"
                >
                  On-demand
                </SelectItem>
              </SelectContent>
            </Select>
            {pricing === "ondemand" && (
              <p className="text-[11px] text-muted-foreground">
                All machines in the pool are kept on one region and InfiniBand fabric.
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">GPU Type</Label>
              <Select
                value={gpuType}
                onValueChange={(value) => {
                  if (!value) return;
                  setGpuType(value);
                  setGpuCount(coerceGpuCount(value, gpuCount));
                }}
              >
                <SelectTrigger className="w-full border-border bg-card text-foreground">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-border bg-card">
                  {Object.keys(GPU_CATALOG).map((type) => (
                    <SelectItem
                      key={type}
                      value={type}
                      className="text-foreground/80 focus:bg-accent focus:text-foreground"
                    >
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">GPUs per Node</Label>
              <Select value={gpuCount} onValueChange={(value) => value && setGpuCount(value)}>
                <SelectTrigger className="w-full border-border bg-card text-foreground">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-border bg-card">
                  {(GPU_CATALOG[gpuType] ?? []).map((count) => (
                    <SelectItem
                      key={count}
                      value={String(count)}
                      className="text-foreground/80 focus:bg-accent focus:text-foreground"
                    >
                      {count}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">Target Count</Label>
              <Input
                type="number"
                min={1}
                max={MAX_TARGET_COUNT}
                value={targetCount}
                onChange={(event) => setTargetCount(event.target.value)}
                required
                className="border-border bg-card text-foreground"
              />
            </div>
          </div>

          <ScheduleEditor
            days={activeDays}
            onDaysChange={setActiveDays}
            start={activeStart}
            onStartChange={setActiveStart}
            end={activeEnd}
            onEndChange={setActiveEnd}
            timezone={activeTz}
            onTimezoneChange={setActiveTz}
          />

          <ComboSelector
            gpuType={gpuType}
            gpuCount={Number(gpuCount)}
            selected={allowedCombos}
            onChange={setAllowedCombos}
            enabled={open}
          />

          {packable && <PackFabricsField checked={packFabrics} onChange={setPackFabrics} />}

          {error && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-600">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="border-border text-foreground/70 hover:bg-accent"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createTarget.isPending}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {createTarget.isPending ? "Creating..." : "Add Target"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TargetExpandedContent({
  target,
  currentUserId,
  isAdmin,
}: {
  target: SpotTarget;
  currentUserId: number;
  isAdmin?: boolean;
}) {
  const { data: machines = [], isLoading } = useTargetMachines(target.id, true);

  if (isLoading) {
    return (
      <div className="py-6 text-center text-[12px] text-muted-foreground">Loading machines...</div>
    );
  }

  if (machines.length === 0) {
    return (
      <div className="py-6 text-center text-[12px] text-muted-foreground">
        No machines provisioned yet. The provisioner will create them automatically.
      </div>
    );
  }

  return (
    <MachineTable
      machines={machines}
      currentUserId={currentUserId}
      isAdmin={isAdmin}
      stickyHeaderClassName="bg-card"
    />
  );
}

function EditTargetDialog({ target }: { target: SpotTarget }) {
  const [open, setOpen] = useState(false);
  const [targetCount, setTargetCount] = useState(String(target.target_count));
  const [labelText, setLabelText] = useState(target.label ?? "");
  const [purpose, setPurpose] = useState<PoolPurpose>(target.purpose);
  const [activeDays, setActiveDays] = useState<Set<string>>(parseDays(target.active_days));
  const [activeStart, setActiveStart] = useState(target.active_start);
  const [activeEnd, setActiveEnd] = useState(target.active_end);
  const [activeTz, setActiveTz] = useState(target.active_tz);
  const [allowedCombos, setAllowedCombos] = useState<Set<string>>(
    parseAllowedCombos(target.allowed_combos),
  );
  const [packFabrics, setPackFabrics] = useState(target.pack_fabrics);
  const [error, setError] = useState("");
  const updateTarget = useUpdateSpotTarget();
  const packable = canPackFabrics(target.gpu_count, target.pricing);

  const handleOpen = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      setTargetCount(String(target.target_count));
      setLabelText(target.label ?? "");
      setPurpose(target.purpose);
      setActiveDays(parseDays(target.active_days));
      setActiveStart(target.active_start);
      setActiveEnd(target.active_end);
      setActiveTz(target.active_tz);
      setAllowedCombos(parseAllowedCombos(target.allowed_combos));
      setPackFabrics(target.pack_fabrics);
      setError("");
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setError("");
    if (activeDays.size === 0) {
      setError("Select at least one active day");
      return;
    }
    if (activeStart === activeEnd) {
      setError("Start and end times must differ");
      return;
    }
    updateTarget.mutate(
      {
        id: target.id,
        target_count: Number(targetCount),
        label: labelText || undefined,
        purpose,
        active_days: formatDays(activeDays),
        active_start: activeStart,
        active_end: activeEnd,
        active_tz: activeTz,
        // Empty Set on the wire (`[]`) tells the backend to clear the
        // allow-list (NULL = all combos). A non-empty Set submits the
        // explicit list. Either way we always send the field on edit so
        // the user can drop a previously-set restriction.
        allowed_combos: formatAllowedCombos(allowedCombos),
        ...(packable ? { pack_fabrics: packFabrics } : {}),
      },
      {
        onSuccess: () => setOpen(false),
        onError: (err) => {
          setError(err instanceof Error ? err.message : "Failed to update target");
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger
        render={
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-[12px] text-foreground/60 hover:bg-accent hover:text-foreground"
          />
        }
      >
        Edit
      </DialogTrigger>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto border-border bg-card sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-foreground">
            Edit {target.gpu_count}&times; {target.gpu_type.toUpperCase()} (
            {target.provider.toUpperCase()})
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Update the pool's intended use, label, count, or schedule.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <PurposeSelector
            value={purpose}
            onChange={setPurpose}
            disabled={target.pricing === "spot"}
          />
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Label</Label>
            <Input
              value={labelText}
              onChange={(event) => setLabelText(event.target.value)}
              placeholder="e.g. H100 pool"
              className="border-border bg-card text-foreground placeholder:text-muted-foreground"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Target Count</Label>
            <Input
              type="number"
              min={0}
              max={MAX_TARGET_COUNT}
              value={targetCount}
              onChange={(event) => setTargetCount(event.target.value)}
              required
              className="border-border bg-card text-foreground"
            />
          </div>

          <ScheduleEditor
            days={activeDays}
            onDaysChange={setActiveDays}
            start={activeStart}
            onStartChange={setActiveStart}
            end={activeEnd}
            onEndChange={setActiveEnd}
            timezone={activeTz}
            onTimezoneChange={setActiveTz}
          />

          <ComboSelector
            gpuType={target.gpu_type}
            gpuCount={target.gpu_count}
            selected={allowedCombos}
            onChange={setAllowedCombos}
            enabled={open}
          />

          {packable && <PackFabricsField checked={packFabrics} onChange={setPackFabrics} />}

          {error && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-600">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="border-border text-foreground/70 hover:bg-accent"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateTarget.isPending}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {updateTarget.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Packing groups slots by GPU cluster, which only Nebius 8-GPU spot slots join. */
function canPackFabrics(gpuCount: number, pricing: string): boolean {
  return gpuCount === 8 && pricing === "spot";
}

function PackFabricsField({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="space-y-0.5">
        <Label className="text-xs font-medium text-muted-foreground">Pack fabrics</Label>
        <p className="text-[11px] text-muted-foreground">
          Put machines on the fabric that already has the most, so they can interconnect. A machine
          left alone in its fabric for an hour, unused, is recreated next to the others.
        </p>
      </div>
      <Switch checked={checked} onCheckedChange={(next) => onChange(!!next)} />
    </div>
  );
}

/** "<label>: text" with the timestamp's age as the label, or the bare text. */
function prefixAge(
  text: string,
  timestamp: string | null | undefined,
  label: (age: string) => string,
): string {
  const age = formatAge(timestamp);
  return age ? `${label(age)}: ${text}` : text;
}

/** One truncated status line under a pool's schedule; the full text is the tooltip. */
function PoolStatusLine({ text, className }: { text: string; className: string }) {
  return (
    <div className={cn("mt-0.5 max-w-sm truncate text-[11px]", className)} title={text}>
      {text}
    </div>
  );
}

function SpotTargetCard({ target }: { target: SpotTarget }) {
  const [expanded, setExpanded] = useState(false);
  const { user } = useAuth();
  const canEdit = useCanEditSpotPool();
  const updateTarget = useUpdateSpotTarget();
  const deleteTarget = useDeleteSpotTarget();

  const overTarget = target.total_running > target.total_target;
  const targetMet = target.total_running === target.total_target;

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm">
      {/* Card header */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className={cn(
          "flex w-full flex-col gap-3 px-3 py-3 text-left transition-colors hover:bg-accent sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-5 sm:py-4",
          expanded ? "rounded-t-xl" : "rounded-xl",
        )}
      >
        {/* Left: GPU config + metadata */}
        <div className="flex items-center gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <ProviderLogo provider={target.provider} />
              <span className="font-mono text-[14px] font-semibold text-foreground">
                {target.gpu_count}&times; {target.gpu_type.toUpperCase()}
              </span>
              <TypeBadge source={target.pricing} />
              <PoolPurposeBadge purpose={target.purpose} />
              {target.pack_fabrics && (
                <span
                  className="rounded-md border border-border px-1.5 py-0.5 text-[11px] text-muted-foreground"
                  title="Pack fabrics: machines are placed so they can interconnect"
                >
                  Packed
                </span>
              )}
            </div>
            {target.label && (
              <div className="mt-0.5 text-[11px] text-muted-foreground">{target.label}</div>
            )}
            <div
              className="mt-0.5 text-[11px] text-muted-foreground"
              title="Provisioning window. Machines are only shut down outside it when the Idle Spot Reaper is enabled in Settings; nothing stops at the end time on its own."
            >
              {target.active_days} {target.active_start}&ndash;{target.active_end}{" "}
              {target.active_tz}
            </div>
            {target.ephemeral_until && (
              <div className="mt-1">
                <EphemeralStatusBadge target={target} />
              </div>
            )}
            {target.failure_reason && (
              <PoolStatusLine
                className="text-rose-500"
                text={prefixAge(target.failure_reason, target.failure_at, (age) => `${age} ago`)}
              />
            )}
            {target.capacity_note && (
              <PoolStatusLine
                className="text-amber-600"
                text={prefixAge(
                  target.capacity_note,
                  target.capacity_since,
                  (age) => `Waiting ${age}`,
                )}
              />
            )}
          </div>
        </div>

        {/* Right: stats + controls */}
        <div className="flex items-center gap-4">
          {/* Machine count */}
          <div className="text-right">
            <div className="text-[12px] text-muted-foreground">
              <span
                className={cn(
                  "font-mono font-semibold",
                  targetMet ? "text-emerald-600" : "text-amber-600",
                )}
              >
                {target.total_running}
              </span>
              <span className="text-muted-foreground">/{target.total_target}</span>
              <span className="ml-1 text-muted-foreground">machines</span>
            </div>
          </div>

          {/* Status */}
          <div className="text-right sm:w-24">
            {target.failure_reason ? (
              <ProvisioningLogsDialog
                target={target}
                label="Failed"
                triggerClassName="text-rose-500 decoration-rose-300 hover:text-rose-600 hover:decoration-rose-400"
              />
            ) : !target.enabled ? (
              <span className="text-[12px] text-muted-foreground">Disabled</span>
            ) : !target.in_window ? (
              <span
                className="text-[12px] text-muted-foreground"
                title="Outside the pool's window: nothing is provisioned until it reopens"
              >
                Outside window
              </span>
            ) : overTarget ? (
              <span className="text-[12px] font-medium text-amber-600">Over target</span>
            ) : targetMet ? (
              <span className="text-[12px] font-medium text-emerald-600">Target met</span>
            ) : target.capacity_note ? (
              <ProvisioningLogsDialog target={target} label="No capacity" />
            ) : (
              <ProvisioningLogsDialog target={target} />
            )}
          </div>

          {/* Enabled switch. Read-only while the access setting is in flight
              (canEdit undefined) and for users it locks out. */}
          {canEdit ? (
            /* biome-ignore lint/a11y/noStaticElementInteractions: event isolation wrapper */
            <div
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") event.stopPropagation();
              }}
            >
              <Switch
                checked={target.enabled}
                onCheckedChange={(checked) =>
                  updateTarget.mutate({ id: target.id, enabled: !!checked })
                }
              />
            </div>
          ) : (
            <Switch checked={target.enabled} disabled />
          )}

          {canEdit && (
            <>
              {/* Start now (ephemeral) */}
              {/* biome-ignore lint/a11y/noStaticElementInteractions: event isolation wrapper */}
              <div
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") event.stopPropagation();
                }}
              >
                <EphemeralStartDialog target={target} />
              </div>

              {/* Edit */}
              {/* biome-ignore lint/a11y/noStaticElementInteractions: event isolation wrapper */}
              <div
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") event.stopPropagation();
                }}
              >
                <EditTargetDialog target={target} />
              </div>

              {/* Delete */}
              {/* biome-ignore lint/a11y/noStaticElementInteractions: event isolation wrapper */}
              <div
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") event.stopPropagation();
                }}
              >
                <ConfirmDialog
                  title="Delete this pool?"
                  description="The pool and its configuration will be permanently deleted."
                  confirmLabel="Delete"
                  variant="destructive"
                  onConfirm={() => deleteTarget.mutate(target.id)}
                  trigger={
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-[12px] text-rose-500 hover:bg-rose-50 hover:text-rose-600"
                    >
                      Delete
                    </Button>
                  }
                />
              </div>
            </>
          )}

          {/* Chevron */}
          <svg
            aria-hidden="true"
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform",
              expanded && "rotate-180",
            )}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-border px-3 pb-3 pt-2 sm:px-5 sm:pb-4 sm:pt-3">
          <TargetExpandedContent
            target={target}
            currentUserId={user?.id ?? 0}
            isAdmin={user?.is_admin}
          />
        </div>
      )}
    </div>
  );
}

export function SpotPoolPage() {
  usePageTitle("Pools");
  const { data: targets = [], isLoading } = useSpotTargets();
  const canEdit = useCanEditSpotPool();

  return (
    <div className="px-4 py-4 sm:px-6 sm:py-5">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-[15px] font-semibold text-foreground">Pools</h1>
        {canEdit && <AddTargetDialog />}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <span className="text-sm text-muted-foreground">Loading targets...</span>
        </div>
      ) : targets.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <span className="text-sm text-muted-foreground">No pools configured</span>
        </div>
      ) : (
        <div className="space-y-3">
          {targets.map((target) => (
            <SpotTargetCard key={target.id} target={target} />
          ))}
        </div>
      )}
    </div>
  );
}
